import { now } from 'lodash'
import { TranscodeStatus, TranscodeStatusType } from '@/@types/TranscodeStatusType'
import { AngleMoviePlayInfoType } from '@/store/stores/collectionModule/documents/angleMovie/AngleMoviePlayInfoType'
import DocumentWrapper from '@/store/stores/collectionModule/documents/DocumentWrapper'
import { PlaylistType } from '@/store/stores/collectionModule/documents/GeneralTypes'
import Const from '@/util/Const'
import RadioDataDocument from '@/store/stores/collectionModule/documents/Radio/RadioDataDocument'
import AngleInfoDocument from '@/store/stores/collectionModule/documents/angleMovie/AngleInfoDocument'

/**
 * アングル動画情報を表現するクラス。
 * @see https://pitchbase.atlassian.net/wiki/spaces/SL01/pages/854392903/01+API
 */
export default class AngleMovieInfoDocument extends DocumentWrapper {
  constructor(initProps?: Partial<AngleMovieInfoDocument>) {
    super(initProps as Partial<DocumentWrapper>)
    this.mergeToThis(initProps)
  }

  _path = 'manage/angle_movie/info'

  idAttr = 'angleMovieId'

  /**
   * 試合ID
   */
  matchId: null | string = null

  /**
   * レースID
   */
  get raceId() {
    return this.matchId
  }

  /**
   * アングル動画情報ID
   */
  angleMovieId: null | string = null

  /**
   * アングル動画再生情報
   */
  anglePlayInfo: AngleMoviePlayInfoType | null = null

  /**
   * アングルID
   */
  angleId: null | string = null

  /**
   * このアングル動画情報に関連するアングル情報
   */
  angle: null | undefined | AngleInfoDocument = null

  /**
   * このアングル動画情報に関連するアングル情報を設定する。
   *
   * @param angleInfo アングル情報
   */
  set angleInfo(angleInfo: AngleInfoDocument | null | undefined) {
    this.angle = angleInfo
  }

  /**
   * このアングル動画情報に関連するアングル情報を取得する。
   */
  get angleInfo(): AngleInfoDocument | null | undefined {
    return this.angle
  }

  /**
   * ライブ配信チャンネルID
   */
  channelId: null | string = null

  /**
   * プレイリストパス
   */
  playlistPath = ''

  /**
   * 動画長
   */
  movieLength: null | number = null

  /**
   * 撮影開始時刻（ミリ秒）
   */
  recordingStartTime: null | number = null

  /**
   * 撮影開始時刻の調整時間（ミリ秒）
   */
  adjustedRecordingStartTime: null | number = null

  /**
   * マルチビットレート用のプレイリスト
   */
  multiBitRatePlaylist: Array<PlaylistType> | null = null

  /**
   * トランスコードステータス
   */
  transcodeStatus: TranscodeStatusType | null = null

  /**
   * ライブ配信ステータス
   */
  liveStreamingStatus: 'Idle' | 'Broadcasting' | 'Stopped' | null = null

  /**
   * ハイライト情報。
   * この動画情報をハイライト再生する場合に利用される。
   */
  highlightPlayInfo: {
    /** ハイライト情報ID */
    highlightId: string
  } | null = null

  /**
   * 無線データ情報。
   * 無線データの開始時刻に動画再生位置を遷移する場合に利用される。
   */
  radioPlayInfo: RadioDataDocument | null = null

  /**
   * 音声プレイリストパス
   */
  audioPlaylistPath = ''

  /**
   * ライブ配信されている映像かどうかを判定する
   */
  get isLive() {
    return this.transcodeStatus === TranscodeStatus.Live
  }

  /**
   * 動画アップロード後、変換が完了しているかどうかを判定する
   */
  get isTranscodeCompleted() {
    return this.transcodeStatus === TranscodeStatus.Complete
  }

  /**
   * 映像配信中かどうかを判定する。
   */
  isBroadcasting() {
    if (this.isLive && this.recordingStartTime) {
      return now() - this.recordingStartTime > Const.LIVE_START_MARGIN
    }
    return this.transcodeStatus === TranscodeStatus.Complete
  }

  /**
   * 映像配信中の場合に、その映像をライブ配信しているかどうかを判定する
   */
  isLiveBroadcasting() {
    return this.isBroadcasting() && this.liveStreamingStatus === 'Broadcasting'
  }

  /**
   * 映像配信中の場合に、その映像がライブ配信を終了しているかどうかを判定する
   */
  isLiveStopped() {
    return this.liveStreamingStatus === 'Stopped'
  }

  /**
   * 映像が未配信かどうかを判定する
   */
  isNotBroadcasting() {
    return !this.isBroadcasting()
  }

  /**
   * ハイライトの切り抜きが可能かどうか
   * ブラウザ版のみで利用する処理
   */
  canClipHighlightForPc() {
    return this.isTranscodeCompleted || (this.isLive && this.isLiveStopped())
  }

  /**
   * adjustedRecordingStartTime の値で調整された撮影開始時刻を取得する。
   */
  get recordingStartTimeCalculated() {
    if (this.adjustedRecordingStartTime) {
      return this.adjustedRecordingStartTime
    }
    return this.recordingStartTime || 0
  }

  /**
   * 再生対象のプレイリストのパスを取得する。
   * PDT補正プレイリストを利用する設定となっている場合、PDT補正プレイリストのパスに変換する。
   */
  get playlistUrl() {
    if (this.angleInfo?.usePDTAdjustedPlaylist) {
      return this.playlistPath.replace('_main', '_main_adjusted')
    }
    return this.playlistPath
  }

  /**
   * 再生対象の音声プレイリストのパスを取得する。
   */
  get audioPlaylistUrl() {
    return this.audioPlaylistPath
  }

  /**
   * 動画のタイムコード情報が利用可能かどうかを判定する。
   * true の場合、タイムコードが利用可能。
   * オンボード映像の場合、ライブ配信された動画の場合にタイムコード情報が利用可能となる。
   */
  get videoTrackTimeEnabled() {
    return this.liveStreamingStatus !== null
  }

  /**
   * このクラスの文字列表現を取得する。
   * @return 文字列表現
   */
  toString() {
    return `AngleMovieInfoDocument[matchId: ${this.matchId}, angleId: ${this.angleId}}]`
  }
}
